import { useCallback, useEffect, useMemo, useReducer, useRef, useState, type JSX } from 'react';
import { api } from '../lib/api.ts';
import { emptyChat, groupChatItems, reduceAll, type ChatState } from '../lib/chat.ts';
import { live } from '../lib/live.ts';
import { useImageDrop } from '../lib/drop.ts';
import { prepareImages } from '../lib/images.ts';
import { isWaitingStatus, notifyRunFinished, rootScroller, useDoneTaskCounts, useGitDiffstat, useLiveStatus, useNow, waitElapsed } from '../lib/hooks.ts';
import { EFFORT_KEY, MODEL_KEY, canonicalizeModel, coerceEffort, readStoredEffort, readStoredModel, type EffortId, type ModelId } from '../lib/models.ts';
import type { GitSnapshot, HostPref, Project, Session, StoredEvent } from '../lib/protocol.ts';
import { readStoredProject, storeProject, type ProjectRow } from '../lib/project.ts';
import { Composer, type ComposerHandle } from './Composer.tsx';
import { BacklogView } from './BacklogView.tsx';
import {
  CommandsView,
  ConfigSubTabs,
  ProjectsConfigView,
  UpdatesConfigView,
  VisionView,
  type ConfigSubTab,
} from './ConfigView.tsx';
import { FilesView } from './FilesView.tsx';
import { GitView, GitSubTabs, gitSubtitle, Diffstat, type GitSubTab } from './GitView.tsx';
import { Item, ToolRow } from './Items.tsx';
import { SessionTabs, type SessionTab } from './SessionTabs.tsx';
import { SettingsView } from './SettingsView.tsx';
import { TaskView } from './TaskView.tsx';

type Action = { type: 'events'; events: StoredEvent[] } | { type: 'reset' };

function chatReducer(state: ChatState, action: Action): ChatState {
  if (action.type === 'reset') return emptyChat;
  return reduceAll(state, action.events);
}

export function Chat({
  sessionId,
  tab,
  onBack,
  onOpen,
  onTab,
}: {
  sessionId: string;
  tab: SessionTab;
  onBack: () => void;
  onOpen: (sessionId: string) => void;
  onTab: (tab: SessionTab) => void;
}): JSX.Element {
  const [state, dispatch] = useReducer(chatReducer, emptyChat);
  const [session, setSession] = useState<Session | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(readStoredProject);
  const [error, setError] = useState<string | null>(null);
  const [git, setGit] = useState<GitSnapshot | null>(null);
  const [gitError, setGitError] = useState<string | null>(null);
  const [gitBusy, setGitBusy] = useState(false);
  const [gitSubTab, setGitSubTab] = useState<GitSubTab>('worktree');
  const [configSubTab, setConfigSubTab] = useState<ConfigSubTab>('vision');
  const [model, setModel] = useState<ModelId>(readStoredModel);
  const [effort, setEffort] = useState<EffortId | null>(() => readStoredEffort(readStoredModel()));
  const [hostBusy, setHostBusy] = useState(false);
  const status = useLiveStatus();
  const diffstat = useGitDiffstat(session?.projectId ?? project?.id ?? null);
  const doneTasks = useDoneTaskCounts(session?.projectId ?? project?.id ?? null);
  const waiting = state.running || state.queued > 0 || isWaitingStatus(session?.runStatus);

  const stateRef = useRef(state);
  stateRef.current = state;
  const wasRunning = useRef(false);
  const prefsReady = useRef(false);
  const chatReady = useRef(false);
  const seenItemIds = useRef(new Set<string>());

  const [stuck, setStuck] = useState(true);
  const [taskEditing, setTaskEditing] = useState(false);
  const [taskDock, setTaskDock] = useState<HTMLDivElement | null>(null);
  const stuckRef = useRef(true);
  stuckRef.current = stuck;
  const composerRef = useRef<ComposerHandle>(null);
  const projectRow = projects.find((p) => p.id === project?.id) ?? null;

  // Load the log over REST first, then attach the socket from the sequence we
  // reached. Doing it in this order means a replay can never be discarded as
  // "already seen" because a live event raced ahead of it.
  useEffect(() => {
    let cancelled = false;
    dispatch({ type: 'reset' });
    setSession(null);
    setProject(null);
    setGit(null);
    setGitError(null);
    setStuck(true);
    setTaskEditing(false);
    prefsReady.current = false;
    chatReady.current = false;
    seenItemIds.current = new Set();

    void (async () => {
      try {
        const [meta, events] = await Promise.all([api.session(sessionId), api.events(sessionId, 0)]);
        if (cancelled) return;
        setSession(meta.session);
        setProject(meta.project);
        storeProject(meta.session.projectId);
        setSelectedProjectId(meta.session.projectId);
        for (const item of reduceAll(emptyChat, events).items) seenItemIds.current.add(item.id);
        chatReady.current = true;
        dispatch({ type: 'events', events });
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  useEffect(() => {
    if (!session) return;
    return live.subscribe(sessionId, {
      sinceSeq: () => stateRef.current.lastSeq,
      onEvent: (stored) => dispatch({ type: 'events', events: [stored] }),
      onSynced: () => undefined,
    });
  }, [sessionId, session]);

  useEffect(() => live.onSession((s) => s.id === sessionId && setSession(s)), [sessionId]);

  useEffect(() => {
    void api
      .projects()
      .then(setProjects)
      .catch(() => undefined);
  }, []);

  useEffect(() => live.onDesktop(() => {
    void api.projects().then(setProjects).catch(() => undefined);
  }), []);

  useEffect(() => {
    if (!session || prefsReady.current) return;
    prefsReady.current = true;
    if (session.model && canonicalizeModel(session.model)) {
      const m = canonicalizeModel(session.model)!;
      setModel(m);
      setEffort(coerceEffort(m, session.effort));
      return;
    }
    const m = readStoredModel();
    const e = readStoredEffort(m);
    setModel(m);
    setEffort(e);
    void api.updateSessionPrefs(sessionId, m, e).then(setSession).catch(() => undefined);
  }, [session, sessionId]);

  const persistPrefs = useCallback(
    (m: ModelId, e: EffortId | null) => {
      localStorage.setItem(MODEL_KEY, m);
      if (e) localStorage.setItem(EFFORT_KEY, e);
      void api.updateSessionPrefs(sessionId, m, e).then(setSession).catch(() => undefined);
    },
    [sessionId],
  );

  const onModel = useCallback(
    (m: ModelId) => {
      const e = coerceEffort(m, effort);
      setModel(m);
      setEffort(e);
      persistPrefs(m, e);
    },
    [effort, persistPrefs],
  );

  const onEffort = useCallback(
    (e: EffortId) => {
      setEffort(e);
      persistPrefs(model, e);
    },
    [model, persistPrefs],
  );

  const onPreferredHost = useCallback(
    (preferredHost: HostPref) => {
      const id = project?.id;
      if (!id) return;
      if ((projectRow?.preferredHost ?? 'desktop') === preferredHost) return;
      setHostBusy(true);
      setProjects((prev) =>
        prev.map((p) => {
          if (p.id !== id) return p;
          const activeHost =
            preferredHost === 'server' || !p.desktopOnline
              ? 'server'
              : (p.desktop?.host ?? 'server');
          return { ...p, preferredHost, activeHost };
        }),
      );
      void api
        .setPreferredHost(id, preferredHost)
        .then((next) => {
          setProjects((prev) => prev.map((p) => (p.id === next.id ? { ...p, ...next } : p)));
        })
        .catch(() => {
          void api.projects().then(setProjects).catch(() => undefined);
        })
        .finally(() => setHostBusy(false));
    },
    [project?.id, projectRow?.preferredHost],
  );

  const loadGit = useCallback(async (): Promise<void> => {
    if (!project) return;
    setGitBusy(true);
    try {
      const snap = await api.git(project.id);
      setGit(snap);
      setGitError(null);
    } catch (err) {
      setGitError(err instanceof Error ? err.message : String(err));
    } finally {
      setGitBusy(false);
    }
  }, [project]);

  useEffect(() => {
    if (!project) return;
    let cancelled = false;
    setGitBusy(true);
    void api
      .git(project.id)
      .then((snap) => {
        if (cancelled) return;
        setGit(snap);
        setGitError(null);
      })
      .catch((err) => {
        if (!cancelled) setGitError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setGitBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [project, projectRow?.activeHost]);

  // A short buzz and chime when a run ends is the whole point of a phone
  // client: you can put it in your pocket and still notice the build finish.
  useEffect(() => {
    if (wasRunning.current && !state.running) {
      notifyRunFinished();
      void loadGit();
    }
    wasRunning.current = state.running;
  }, [state.running, loadGit]);

  useEffect(() => {
    if (tab === 'chat' && stuckRef.current) {
      const el = rootScroller();
      el.scrollTop = el.scrollHeight;
    } else if (tab === 'git' || tab === 'settings' || tab === 'task' || tab === 'config') {
      rootScroller().scrollTop = 0;
    }
  }, [state.items, tab, session?.task]);

  useEffect(() => {
    const onScroll = (): void => {
      const el = rootScroller();
      setStuck(el.scrollHeight - el.scrollTop - el.clientHeight < 90);
    };
    addEventListener('scroll', onScroll, { passive: true });
    return () => removeEventListener('scroll', onScroll);
  }, []);

  const send = useCallback(
    async (text: string, images: { mime: string; data: string }[] = []) => {
      setStuck(true);
      onTab('chat');
      try {
        await api.send(sessionId, text, model, effort, images);
        // With the socket down the POST still worked; pull the tail by hand so
        // the message and its answer are not invisible until reconnect.
        if (live.status !== 'live') {
          const events = await api.events(sessionId, stateRef.current.lastSeq);
          dispatch({ type: 'events', events });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    },
    [sessionId, model, effort, onTab],
  );

  const setSkipVerification = useCallback(
    (skip: boolean) => {
      setSession((prev) => (prev ? { ...prev, skipVerification: skip } : prev));
      void api
        .setSkipVerification(sessionId, skip)
        .then(setSession)
        .catch((err) => {
          setError(err instanceof Error ? err.message : String(err));
          void api
            .session(sessionId)
            .then((meta) => setSession(meta.session))
            .catch(() => undefined);
        });
    },
    [sessionId],
  );

  const setLogging = useCallback(
    (logging: boolean) => {
      setSession((prev) => (prev ? { ...prev, logging } : prev));
      void api
        .setLogging(sessionId, logging)
        .then(setSession)
        .catch((err) => {
          setError(err instanceof Error ? err.message : String(err));
          void api
            .session(sessionId)
            .then((meta) => setSession(meta.session))
            .catch(() => undefined);
        });
    },
    [sessionId],
  );

  const saveTask = useCallback(
    async (
      text: string,
      images?: { mime: string; data: string; id?: string }[],
      surface?: string[],
      title?: string,
    ): Promise<boolean> => {
      const body = text.trim();
      if (images !== undefined && !body && images.length === 0) return false;
      setStuck(true);
      try {
        setSession(await api.setTask(sessionId, body, images, surface, title));
        return true;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return false;
      }
    },
    [sessionId],
  );

  const addTaskImages = useCallback(
    async (files: File[]): Promise<void> => {
      const existing = session?.taskImages ?? [];
      const { images: next, error } = await prepareImages(files, existing.length);
      if (next.length === 0) {
        if (error) throw new Error(error);
        return;
      }
      const ok = await saveTask(session?.task ?? '', [
        ...existing.map((img) => ({ mime: img.mime, data: '', id: img.id })),
        ...next.map(({ mime, data }) => ({ mime, data })),
      ]);
      if (!ok) throw new Error('Could not save photos.');
      if (error) throw new Error(error);
    },
    [session, saveTask],
  );

  const chatDrop = useImageDrop(
    (files) => void composerRef.current?.addFiles(files),
    tab === 'chat',
  );

  const stop = useCallback(() => {
    void api
      .cancel(sessionId)
      .then((result) => {
        if (result.session) setSession(result.session);
      })
      .catch(() => undefined);
  }, [sessionId]);

  const commit = useCallback(
    async (message: string) => {
      if (!project) throw new Error('no project');
      setGit(await api.commit(project.id, message));
    },
    [project],
  );

  const push = useCallback(async () => {
    if (!project) throw new Error('no project');
    setGit(await api.push(project.id));
  }, [project]);

  const items = useMemo(() => state.items, [state.items]);
  const blocks = useMemo(() => groupChatItems(items), [items]);
  const entering = new Set<string>();
  if (chatReady.current) {
    for (const item of items) {
      if (item.type === 'assistant' && !seenItemIds.current.has(item.id)) {
        entering.add(item.id);
        seenItemIds.current.add(item.id);
      }
    }
  }
  const lastSubmitAt = useMemo(() => {
    let fromItems: number | null = null;
    for (let i = items.length - 1; i >= 0; i--) {
      if (items[i]!.type === 'user') {
        fromItems = items[i]!.ts;
        break;
      }
    }
    const fromSession = session?.lastSubmittedAt ?? null;
    if (fromItems != null && fromSession != null) return Math.max(fromItems, fromSession);
    return fromItems ?? fromSession;
  }, [items, session?.lastSubmittedAt]);
  const endedAt = state.lastRunEndedAt ?? session?.lastEndedAt ?? null;
  const now = useNow(
    waiting && !(endedAt != null && lastSubmitAt != null && endedAt >= lastSubmitAt),
  );
  const waitLabel = waitElapsed(lastSubmitAt, endedAt, waiting, now);
  const showCommands = Boolean(projectRow?.desktop && projectRow.desktopOnline);
  const showBacklog = tab === 'config' && configSubTab === 'backlog';

  return (
    <div
      className={
        (!showBacklog && tab === 'config') || tab === 'git' || (tab === 'task' && taskEditing)
          ? 'screen screen-pinned'
          : 'screen'
      }
    >
      <header className="topbar">
        <div className="topbar-title">
          <div className="topbar-name">{session?.title ?? 'Loading...'}</div>
          <div className="topbar-sub">
            <span className={`dot dot-${status}`} aria-label={status} />
            {project?.name ?? session?.projectId ?? ''}
            {git ? ` · ${gitSubtitle(git)}` : ''}
            {waitLabel ? (
              <>
                {' · '}
                <span className="elapsed" title={waiting ? 'Time since last prompt' : 'Last wait'}>
                  {waitLabel}
                </span>
              </>
            ) : null}
          </div>
        </div>
        <Diffstat stat={diffstat} />
      </header>

      <div
        className={`log${chatDrop.over ? ' is-drop-target' : ''}`}
        id="pane-chat"
        role="tabpanel"
        aria-labelledby="tab-chat"
        hidden={tab !== 'chat'}
        {...chatDrop.handlers}
      >
        {items.length === 0 && !error && (
          <div className="empty">
            <p>
              This chat runs <code>cursor-agent</code> in
              <br />
              <code>{project?.path ?? '...'}</code>
            </p>
            <p className="empty-hint">Ask it to explore the repo, fix a test, or open a branch.</p>
          </div>
        )}
        {blocks.map((block) =>
          block.type === 'tools' ? (
            <ToolRow key={block.id} items={block.items} />
          ) : (
            <Item key={block.id} item={block} sessionId={sessionId} enter={entering.has(block.id)} />
          ),
        )}
        {state.running && !items.some((i) => i.type === 'assistant' && i.streaming) && (
          <div className="thinking">
            <span className="spinner" /> working
            {waitLabel ? (
              <>
                {' · '}
                <span className="elapsed" title="Time since last prompt">
                  {waitLabel}
                </span>
              </>
            ) : null}
          </div>
        )}
        {error && <div className="note note-error">{error}</div>}
        {chatDrop.over && (
          <div className="drop-overlay" aria-hidden="true">
            Drop photos
          </div>
        )}
      </div>

      <TaskView
        hidden={tab !== 'task'}
        task={session?.task ?? ''}
        title={session?.title ?? ''}
        taskImages={session?.taskImages ?? []}
        sessionId={sessionId}
        model={model}
        effort={effort}
        onModel={onModel}
        onEffort={onEffort}
        onSave={(text, title) => saveTask(text, undefined, undefined, title)}
        onDropImages={addTaskImages}
        onEditingChange={setTaskEditing}
        skipVerification={session?.skipVerification ?? false}
        onSkipVerification={setSkipVerification}
        dock={tab === 'task' && !taskEditing ? taskDock : null}
      />

      {tab === 'chat' && !stuck && (
        <button
          type="button"
          className="jump"
          onClick={() => {
            setStuck(true);
            const el = rootScroller();
            el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
          }}
          aria-label="Jump to latest"
        >
          &darr;
        </button>
      )}

      <GitView
        hidden={tab !== 'git'}
        projectId={project?.id ?? ''}
        snap={git}
        error={gitError}
        busy={gitBusy}
        subTab={gitSubTab}
        onRefresh={() => void loadGit()}
        onCommit={commit}
        onPush={push}
        onSnapshot={setGit}
      />

      <SettingsView
        hidden={tab !== 'settings'}
        model={model}
        effort={effort}
        project={projectRow}
        hostBusy={hostBusy}
        logging={session?.logging ?? false}
        onModel={onModel}
        onEffort={onEffort}
        onPreferredHost={onPreferredHost}
        onLogging={setLogging}
      />

      <BacklogView
        hidden={!showBacklog}
        projectId={project?.id ?? selectedProjectId}
        projectName={project?.name}
      />

      {project && (
        <VisionView
          hidden={tab !== 'config' || configSubTab !== 'vision'}
          projectId={project.id}
          host={projectRow?.activeHost}
          onOpen={onOpen}
        />
      )}

      {project && (
        <FilesView
          hidden={tab !== 'config' || configSubTab !== 'files'}
          projectId={project.id}
          host={projectRow?.activeHost}
        />
      )}

      <ProjectsConfigView hidden={tab !== 'config' || configSubTab !== 'projects'} />

      <UpdatesConfigView hidden={tab !== 'config' || configSubTab !== 'updates'} />

      {projectRow && (
        <CommandsView
          hidden={tab !== 'config' || configSubTab !== 'commands'}
          project={projectRow}
        />
      )}

      <div className="dock">
        {tab === 'git' && (
          <GitSubTabs tab={gitSubTab} dirty={git?.dirty === true} onTab={setGitSubTab} />
        )}
        {tab === 'config' && (
          <ConfigSubTabs tab={configSubTab} onTab={setConfigSubTab} showCommands={showCommands} />
        )}
        <div
          ref={setTaskDock}
          className="task-dock"
          hidden={tab !== 'task' || taskEditing}
        />
        {tab === 'chat' && (
          <Composer
            key={sessionId}
            ref={composerRef}
            sessionId={sessionId}
            running={
              state.running ||
              ((session?.runStatus === 'queued' || session?.runStatus === 'paused') &&
                state.queued === 0)
            }
            queued={state.queued}
            busyKind={state.running ? 'running' : 'queued'}
            stopLabel={
              state.running
                ? 'Pause the agent'
                : session?.runStatus === 'paused'
                  ? 'Drop this turn instead of waiting'
                  : session?.runStatus === 'queued'
                    ? 'Remove from queue'
                    : 'Stop the agent'
            }
            onSend={(text, images) => void send(text, images)}
            onStop={stop}
          />
        )}
        <SessionTabs
          tab={tab}
          inSession
          doneTasks={doneTasks.selected}
          doneTasksOther={doneTasks.other}
          gitDirty={git?.dirty === true}
          projects={projects}
          selectedProjectId={selectedProjectId}
          doneByProject={doneTasks.byProject}
          onSelectProject={(id) => {
            storeProject(id);
            setSelectedProjectId(id);
            if (id === project?.id) onTab('chat');
            else onBack();
          }}
          onTab={onTab}
        />
      </div>
    </div>
  );
}
